import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useLocalAgentId } from "../hooks/useLocalAgentId";

import AppShell from "../components/AppShell";
import ProvisioningNotice from "../components/ProvisioningNotice";
import { StatusDot, StatusPill } from "../components/StatusDot";
import { WarningIcon, BlockedIcon, InfoIcon } from "../components/Icons";
import { listAgents, type AgentRow } from "../api/agents";
import { listPrinters, type PrinterRow } from "../api/printers";
import { formatRelative } from "../utils/format";
import { isHostUnavailable } from "../utils/agent";

const POLL_MS = 30_000;

export default function Dashboard() {
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [printers, setPrinters] = useState<PrinterRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const localAgentId = useLocalAgentId();

  useEffect(() => {
    let cancelled = false;
    async function go() {
      try {
        const [agentData, printerData] = await Promise.all([
          listAgents(),
          listPrinters(),
        ]);
        if (!cancelled) {
          setAgents(agentData.results);
          setPrinters(printerData.results);
          setError(null);
        }
      } catch {
        if (!cancelled) setError("Couldn't load office status. We'll try again shortly.");
      }
    }
    go();
    const id = setInterval(go, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const visibleAgents = agents?.filter((a) => a.status !== "uninstalled") ?? [];
  const onlineCount = visibleAgents.filter((a) => a.status === "online").length;
  const updateCount = visibleAgents.filter((a) => a.update_available).length;
  const visiblePrinters = printers?.filter((p) => p.status !== "removed") ?? [];
  const sharedPrinters = visiblePrinters.filter((p) => p.status === "active");
  const agentMap = useMemo(
    () => new Map<string, AgentRow>(agents?.map((a) => [a.id, a]) ?? []),
    [agents],
  );

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl space-y-8">
        {/* Page header */}
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Office Overview</h1>
            {agents !== null && (
              <p className="mt-1 text-sm text-slate-500">
                {visibleAgents.length === 0
                  ? "No computers connected yet"
                  : `${onlineCount} of ${visibleAgents.length} computer${visibleAgents.length !== 1 ? "s" : ""} online${
                      sharedPrinters.length > 0
                        ? ` · ${sharedPrinters.length} printer${sharedPrinters.length !== 1 ? "s" : ""} shared`
                        : ""
                    }`}
              </p>
            )}
          </div>
          <Link
            to="/computers/add"
            className="no-tap-highlight shrink-0 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Add a computer
          </Link>
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        )}

        {updateCount > 0 && (
          <div className="flex items-start gap-3 rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <svg className="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.25a.75.75 0 00-1.5 0v4.59L7.3 9.24a.75.75 0 00-1.1 1.02l3.25 3.5a.75.75 0 001.1 0l3.25-3.5a.75.75 0 10-1.1-1.02l-1.95 2.1V6.75z" clipRule="evenodd" />
            </svg>
            <span>
              <span className="font-medium">
                {updateCount === 1 ? "1 computer has" : `${updateCount} computers have`} an update ready.
              </span>
              {" "}They'll update automatically — no action needed.
            </span>
          </div>
        )}

        {/* Computers section */}
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Computers
          </h2>
          {agents === null ? (
            <SkeletonList count={3} />
          ) : visibleAgents.length === 0 ? (
            <EmptyComputers />
          ) : (
            <div className="space-y-3">
              {visibleAgents.map((agent) => (
                <ComputerCard key={agent.id} agent={agent} isLocal={agent.id === localAgentId} />
              ))}
            </div>
          )}
        </section>

        {/* Printers section */}
        {(printers === null || visiblePrinters.length > 0) && (
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Printers
              </h2>
              {visiblePrinters.length > 0 && (
                <Link
                  to="/sharing/printers"
                  className="text-xs text-slate-500 underline hover:text-slate-900"
                >
                  Manage printers
                </Link>
              )}
            </div>
            {printers === null ? (
              <SkeletonList count={1} />
            ) : (
              <div className="space-y-3">
                {visiblePrinters.map((printer) => (
                  <PrinterCard
                    key={printer.id}
                    printer={printer}
                    hostAgent={agentMap.get(printer.host_agent) ?? null}
                  />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}

function UpdateBadge() {
  return (
    <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700">
      Update available
    </span>
  );
}

function ComputerCard({ agent, isLocal }: { agent: AgentRow; isLocal: boolean }) {
  const navigate = useNavigate();
  const isOffline = agent.status === "offline";
  const isBlocked = agent.status === "blocked";
  const needsAttention = agent.status === "needs_attention";
  const isProvisioning = agent.status === "provisioning";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/computers/${agent.id}`)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/computers/${agent.id}`); }}
      className={`cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
        isOffline || isBlocked ? "opacity-70" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="mt-1 shrink-0">
          <StatusDot status={agent.status} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-base font-semibold text-slate-900">
                {agent.label || agent.hostname}
              </div>
              <div className="mt-0.5 text-sm text-slate-500">
                {agent.os}
                {agent.last_seen_at && (
                  <> · last seen {formatRelative(agent.last_seen_at)}</>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {agent.update_available && <UpdateBadge />}
              {isLocal && <ThisComputerBadge />}
              <StatusPill status={agent.status} />
            </div>
          </div>

          {agent.tailscale_ip && agent.status === "online" && (
            <div className="mt-2 inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-500">
              <span className="font-mono">{agent.tailscale_ip}</span>
            </div>
          )}

          {isOffline && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
              <WarningIcon />
              <div>
                <span className="font-medium">Can't connect</span> — this computer is currently off.
                <br />
                <span className="text-amber-700">
                  Turn it on to access its shared files and printers.
                </span>
              </div>
            </div>
          )}

          {isBlocked && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-orange-50 px-3 py-2.5 text-sm text-orange-800">
              <BlockedIcon />
              <div>
                <span className="font-medium">Blocked</span> — this computer is not allowed to
                connect to the office network.
              </div>
            </div>
          )}

          {needsAttention && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-800">
              <InfoIcon />
              <div>
                <span className="font-medium">Needs attention</span> — KraetdNet may not be running
                correctly on this computer. Try restarting it.
              </div>
            </div>
          )}

          {isProvisioning && (
            <ProvisioningNotice
              agentId={agent.id}
              createdAt={agent.created_at}
              withinClickableParent
            />
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end gap-1 text-xs text-slate-400">
        View shared files &amp; printers
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
        </svg>
      </div>
    </div>
  );
}

function PrinterCard({
  printer,
  hostAgent,
}: {
  printer: PrinterRow;
  hostAgent: AgentRow | null;
}) {
  const hostName = printer.host_agent_label || printer.host_agent_hostname;
  const hostIsOffline = isHostUnavailable(hostAgent);
  const isShared = printer.status === "active";
  const isPending = printer.status === "pending";
  const isFailed = printer.status === "failed";
  const isRemoving = printer.status === "removing";
  const isDetected = printer.status === "detected";

  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-5 ${
        isShared && hostIsOffline ? "opacity-70" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="mt-0.5 shrink-0">
          <PrinterIcon active={isShared && !hostIsOffline} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-base font-semibold text-slate-900">
                {printer.label || printer.printer_name}
              </div>
              <div className="mt-0.5 text-sm text-slate-500">
                On <span className="font-medium">{hostName}</span>
                {printer.driver ? <> · {printer.driver}</> : null}
              </div>
            </div>

            <div className="shrink-0">
              {isShared && !hostIsOffline && (
                <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
                  Ready to print
                </span>
              )}
              {isDetected && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                  Not shared
                </span>
              )}
              {isPending && (
                <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                  Setting up…
                </span>
              )}
              {isFailed && (
                <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                  Setup failed
                </span>
              )}
              {isRemoving && (
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                  Removing…
                </span>
              )}
            </div>
          </div>

          {isShared && hostIsOffline && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
              <WarningIcon />
              <div>
                <span className="font-medium">Can't print right now</span> — {hostName} is off.
                <br />
                <span className="text-amber-700">Turn {hostName} on to use this printer.</span>
              </div>
            </div>
          )}

          {isDetected && (
            <div className="mt-2 text-xs text-slate-400">
              Go to{" "}
              <Link to="/sharing/printers" className="underline hover:text-slate-700">
                Printers
              </Link>{" "}
              to share it with the office.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SkeletonList({ count }: { count: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-start gap-4">
            <div className="mt-1 h-3 w-3 animate-pulse rounded-full bg-slate-200" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/3 animate-pulse rounded bg-slate-200" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyComputers() {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
        <svg
          className="h-6 w-6 text-slate-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0H3"
          />
        </svg>
      </div>
      <h2 className="mt-4 text-base font-semibold text-slate-900">No computers yet</h2>
      <p className="mt-2 text-sm text-slate-500">
        Install KraetdNet on each computer in your office to connect them.
      </p>
      <Link
        to="/computers/add"
        className="no-tap-highlight mt-4 inline-block rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
      >
        Add your first computer
      </Link>
    </div>
  );
}

function ThisComputerBadge() {
  return (
    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
      This computer
    </span>
  );
}

function PrinterIcon({ active }: { active: boolean }) {
  return (
    <svg
      className={`h-5 w-5 ${active ? "text-emerald-500" : "text-slate-400"}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.056 48.056 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z"
      />
    </svg>
  );
}

